import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import BottomNav from '../components/BottomNav';
import ProductGallery from '../components/ProductGallery';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Droplets, ShoppingCart, Minus, Plus, 
  Check, Info, Shield, Zap
} from 'lucide-react';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const { addToCart, cartCount } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const res = await api().get(`/products/${id}`);
        setProduct(res.data);
      } catch (err) {
        toast.error('Produto não encontrado');
        navigate('/catalog');
      }
      setLoading(false);
    };
    loadProduct();
  }, [api, id, navigate]);

  const handleAddToCart = async () => {
    setAdding(true);
    try {
      await addToCart(product.id, quantity);
      toast.success(`${quantity}x ${product.name} adicionado ao cesto!`);
    } catch (err) {
      toast.error('Erro ao adicionar ao cesto');
    }
    setAdding(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-sonangol-red border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!product) return null;

  const baseTypeLabel = {
    synthetic: 'Sintético',
    semi_synthetic: 'Semi-Sintético',
    mineral: 'Mineral'
  };

  return (
    <div className="min-h-screen bg-zinc-100 pb-32">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-zinc-100 px-4 py-3">
        <div className="flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)} 
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-zinc-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <button 
            onClick={() => navigate('/cart')} 
            className="relative w-10 h-10 bg-zinc-100 rounded-xl flex items-center justify-center"
            data-testid="cart-btn"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-sonangol-red text-white text-xs font-bold rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Product Image */}
      <div className="bg-gradient-to-br from-zinc-100 to-zinc-200 pt-8 pb-12 px-4">
        <ProductGallery product={product} />
      </div>

      {/* Product Info */}
      <div className="px-4 -mt-6">
        <div className="bg-white rounded-2xl shadow-sm p-5 border border-zinc-100">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <Badge className="bg-sonangol-orange/10 text-sonangol-orange border-0 mb-2">
                {product.category.toUpperCase()}
              </Badge>
              <h1 className="font-barlow font-bold text-2xl text-zinc-800">{product.name}</h1>
            </div>
            <Badge variant="outline" className="text-sm">
              {product.volume_liters}L
            </Badge>
          </div>

          <p className="text-zinc-600 font-manrope text-sm leading-relaxed mb-4">
            {product.description}
          </p>

          {/* Specs Grid */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-zinc-50 rounded-xl p-3 text-center">
              <p className="text-xs text-zinc-500 font-manrope">Viscosidade</p>
              <p className="font-barlow font-bold text-lg text-zinc-800">{product.viscosity}</p>
            </div>
            <div className="bg-zinc-50 rounded-xl p-3 text-center">
              <p className="text-xs text-zinc-500 font-manrope">Base</p>
              <p className="font-barlow font-bold text-sm text-zinc-800">{baseTypeLabel[product.base_type]}</p>
            </div>
            <div className="bg-zinc-50 rounded-xl p-3 text-center">
              <p className="text-xs text-zinc-500 font-manrope">Stock</p>
              <p className="font-barlow font-bold text-lg text-green-600">{product.stock}</p>
            </div>
          </div>

          {/* Benefits */}
          {product.benefits?.length > 0 && (
            <div className="mb-4">
              <h3 className="font-barlow font-semibold text-zinc-800 mb-2 flex items-center gap-2">
                <Zap className="w-4 h-4 text-sonangol-orange" />
                Benefícios
              </h3>
              <ul className="space-y-2">
                {product.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-zinc-600 font-manrope">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Specifications */}
          {product.specifications && Object.keys(product.specifications).length > 0 && (
            <div>
              <h3 className="font-barlow font-semibold text-zinc-800 mb-2 flex items-center gap-2">
                <Shield className="w-4 h-4 text-blue-500" />
                Especificações
              </h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(product.specifications).map(([key, value]) => (
                  <Badge key={key} variant="outline" className="text-xs">
                    {key}: {value}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Bar */}
      <div className="fixed above-nav left-0 right-0 bg-white border-t border-zinc-200 px-4 py-4 z-30">
        <div className="flex items-center gap-4">
          {/* Quantity Selector */}
          <div className="flex items-center bg-zinc-100 rounded-xl">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              className="w-10 h-10 flex items-center justify-center text-zinc-600 hover:text-zinc-800"
              data-testid="quantity-minus"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="w-10 text-center font-barlow font-bold" data-testid="quantity-display">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity(quantity + 1)}
              className="w-10 h-10 flex items-center justify-center text-zinc-600 hover:text-zinc-800"
              data-testid="quantity-plus"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Add to Cart Button */}
          <Button
            className="flex-1 h-12 bg-sonangol-red hover:bg-red-700 font-barlow font-bold uppercase tracking-wider"
            onClick={handleAddToCart}
            disabled={adding}
            data-testid="add-to-cart-btn"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            {(product.price_aoa * quantity).toLocaleString('pt-AO')} AOA
          </Button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default ProductDetailPage;
